<script setup lang="ts">
  import { onMounted, ref } from "vue";
  import HeaderCheckout from "@/components/HeaderCheckout.vue";
  import ContactBox from "@/components/ContactBox.vue";
  import CartProduct from "@/components/CartProduct.vue";
  import LoadSpinner from "@/components/LoadSpinner.vue";
  import ShippingItem from "@/components/ShippingItem.vue";
  import IconLoader from "@/icons/IconLoader.vue";
  import IconBag from "@/icons/IconBag.vue";
  import moneyFormat from "./utils/moneyFormat";
  import {useCartStore} from "@/stores/cartStore";
  import DiscountBlock from "@/components/DiscountBlock.vue";
  import {setCookie} from "@/utils/cookies";
  import IconArrow from "@/icons/IconArrow.vue";

  const cartStore = useCartStore();

  const showSummary = ref(false);

  const savedAddresses = ref([]);

  async function updateState() {
    await cartStore.loadState();
    await cartStore.getCart(true);
    await cartStore.getCountries();

    const localStorageAddresses = localStorage.getItem('_cke-addresses-vue');
    if(localStorageAddresses) {
      savedAddresses.value = JSON.parse(localStorageAddresses);
    }
  }

  onMounted(async () => {
    updateState();

    document.getElementById('shopify-section-announcement')?.remove();
    document.getElementById('shopify-section-header')?.remove();
    document.getElementById('shopify-section-footer')?.remove();
    document.getElementById('breadcrumbs-id')?.remove();
    document.body.classList.add('is-active');

    window.addEventListener('beforeunload', function () {
      if(cartStore.cartToken) {
        setCookie('cart', cartStore.cartToken, 14);
      }
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        location.reload();
      }
    });
  });

  const handleToggleSummary = () => {
    showSummary.value = !showSummary.value;
  }

  const handleSubmitOrder = () => {
    if(!cartStore.loading && !cartStore.submitting) {
      cartStore.createDraftOrderAndRedirect();
    }
  }
</script>

<template>
  <div class="PageContainer" :class="{ 'disabled': cartStore.submitting }">
    <div id="shopify-section-main-shipping" class="shopify-section">
      <main id="main">
        <section class="section-main-shipping">
          <HeaderCheckout />
          <div class="Container">
            <div class="PageContent">
              <div class="page-columns">
                <div class="column">
                  <ContactBox/>
                  <div :class="{ 'Cart Cart--expanded loading': true, 'is-loading': cartStore.loading }">
                    <div class="Cart__ItemList" :style="{ minHeight: '420px' }">
                      <CartProduct v-for="(lineItem, index) in cartStore.lineItems"
                                   :key="lineItem.key"
                                   :lineItem="lineItem"
                                   :index="index" />
                    </div>
                    <LoadSpinner/>
                  </div>
                  <div class="discount-box-wrap discount-box-wrap-mobile">
                    <DiscountBlock :line-items="cartStore.lineItems"/>
                  </div>
                </div>
                <div class="column small">
                  <div class="checkout-total">
                    <div :class="{ 'checkout-total__head': true, 'is-active': showSummary }" @click="handleToggleSummary">
                      <IconBag/>
                      <span class="checkout-total__ttl">{{ showSummary ? "Hide order summary" : "Show order summary" }}</span>
                      <IconArrow />
                      <span class="checkout-total__sum">{{ moneyFormat(cartStore.cartTotal) }}</span>
                    </div>
                    <div :class="{ 'checkout-total__body': true, 'is-active': showSummary }">
                      <div class="list-products">
                        <ShippingItem v-for="(lineItem, index) in cartStore.lineItems" :key="lineItem.id" :lineItem="lineItem" :index="index" />
                      </div>
                      <div class="checkout-total__footer Cart Cart--expanded">
                        <div class="Cart__loader-overview"><IconLoader/></div>
                        <div class="discount-box-wrap discount-box-wrap-desktop">
                          <DiscountBlock :line-items="cartStore.lineItems"/>
                        </div>
                        <div class="checkout-total__info">
                          <div class="checkout-total__info-itm">
                            Subtotal <div class="checkout-total__info-sum">{{ moneyFormat(cartStore.cartSubtotal) }}</div>
                          </div>

                          <div class="checkout-total__info-itm">
                            Shipping <div class="checkout-total__info-sum">{{ moneyFormat(cartStore.cartShipping) }}</div>
                          </div>

                          <template v-if="cartStore.discountData.success && cartStore.cartDiscount">
                            <div class="checkout-total__info-itm checkout-total__info-itm--discount">
                              Discount <div class="checkout-total__info-sum">{{ moneyFormat(-cartStore.cartDiscount) }}</div>
                            </div>
                            <div class="checkout-total__info-itm checkout-total__info-itm_discount">
                              <p class="list-productsl__discount">{{ cartStore.discountData.code }} (-{{ moneyFormat(cartStore.cartDiscount) }})</p>
                            </div>
                          </template>

                          <div class="checkout-total__footnote">
                            Taxes calculated at checkout
                          </div>
                        </div>
                        <div class="checkout-total__sum-total">
                          GRAND TOTAL
                          <span data-total-type="grand-total">{{ moneyFormat(cartStore.cartTotal) }}</span>
                        </div>
                        <div class="checkout-total__buttons checkout-total__buttons-top">
                          <p class="form__label-error">* Please fill in all required fields and confirm shipping information to continue</p>
                          <button type="button" class="Button Button--primary Button--full" :class="{ 'disabled': cartStore.submitting }" :disabled="!cartStore.isLineItemsFilled" @click="handleSubmitOrder">
                            Continue to Payment
                            <span class="jumping-dots" v-if="cartStore.submitting">
                              <span class="jumping-dot">.</span> <span class="jumping-dot">.</span> <span class="jumping-dot">.</span>
                            </span>
                          </button>
                          <a href="/cart" class="Button">Back to Cart </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="checkout-total__buttons checkout-total__buttons--hidden-tablet">
                  <p class="form__label-error">* Please fill in all required fields and confirm shipping information to continue</p>
                  <button type="button" class="Button Button--primary Button--full" :class="{ 'is-block': cartStore.submitting || cartStore.loading, 'disabled': cartStore.submitting || cartStore.loading }" :disabled="!cartStore.isLineItemsFilled || cartStore.loading" @click="handleSubmitOrder">
                    <IconLoader />
                    Continue to Payment
                  </button>
                  <a href="/cart" class="Button">Back to Cart </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>